import React from 'react'
import { createRoot } from 'react-dom/client'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'

import { SalenoteContractTemplate } from '../components/SalenoteContractTemplate'
import { Salenote } from '../interfaces/Salenote'
import UserData from '../interfaces/UserData'
import { Delegate } from '../interfaces/OrganizationDto'
import Consignment from '../interfaces/Consignment'

import { insertAfterFirstToken } from './utils'

// Helper to prepare data for the template
// This logic mirrors SalenoteContract.tsx to prepare the props
export const prepareContractData = (
  salenote: Salenote,
  userData: UserData,
  salenoteTradeIn?: Salenote,
  consignment?: Consignment
) => {
  const organizationObj = userData?.organization
  const delegates: Delegate[] = organizationObj?.delegates ?? []

  let sellerName = ''
  let sellerRut = ''
  let sellerAddr = ''
  let personeria1 = ''
  let personeria2 = ''
  let showRepresentatives = false
  let representadoPorSpan: React.ReactNode = <span>,</span>
  let representado2PorSpan: React.ReactNode = <span></span>

  // Logic to determine seller:
  // Priority:
  // 1. Consignment (Venta consignada)
  // 2. Trade-in (Vehículo en parte de pago)
  // 3. Organization (Stock propio / compra directa)

  if (consignment && consignment.body && consignment.body.client) {
    // Seller is the consignor
    sellerName = `${consignment.body.client.firstname} ${consignment.body.client.lastname}`
    sellerRut = consignment.body.client.nin
    const c1 = consignment.body.client.address
    const c2 = (consignment.body.client.streetNumber ?? '').toString()
    const c3 = consignment.body.client.addressComponents ?? ''
    sellerAddr = insertAfterFirstToken(c1, c2, c3)
  } else if (salenoteTradeIn && salenoteTradeIn.body && salenoteTradeIn.body.client) {
    // Seller is the client from the trade-in note
    sellerName = `${salenoteTradeIn.body.client.firstname} ${salenoteTradeIn.body.client.lastname}`
    sellerRut = salenoteTradeIn.body.client.nin
    const c1 = salenoteTradeIn.body.client.address
    const c2 = (salenoteTradeIn.body.client.streetNumber ?? '').toString()
    const c3 = salenoteTradeIn.body.client.addressComponents ?? ''
    sellerAddr = insertAfterFirstToken(c1, c2, c3)
  } else {
    // Defaulting to Organization (Automotora) as seller
    showRepresentatives = delegates.length > 0
    sellerName = organizationObj?.legalName ?? organizationObj?.name ?? ''
    sellerRut = organizationObj?.nin ?? ''

    const c1 = organizationObj?.address
    const c2 = (organizationObj?.streetNumber ?? '').toString()
    const c3 = organizationObj?.addressComponents ?? ''
    const fullAddress = insertAfterFirstToken(c1, c2, c3)
    sellerAddr = fullAddress

    if (delegates.length > 0) {
      personeria1 = `${delegates[0].firstname} ${delegates[0].lastname}`
      representadoPorSpan = (
        <span>
          , representado por{' '}
          <span className="font-bold underline">
            {personeria1} RUT {delegates[0].nin}
          </span>
        </span>
      )
    }

    if (delegates.length >= 2) {
      personeria2 = `${delegates[1].firstname} ${delegates[1].lastname}`
      representado2PorSpan = (
        <span className="font-bold underline">
          {' '}
          y {personeria2} RUT {delegates[1].nin}
        </span>
      )
    }
  }

  // Plate formatting
  const plate = salenote.body.vehicle.plate
  // We assume plate DV calculation is handled or raw plate is fine for now
  const vehPlateSpan = <span className="font-bold underline">{plate}</span>

  return {
    salenote,
    userData,
    sellerName,
    sellerRut,
    sellerAddr,
    delegates,
    showRepresentatives,
    personeria1,
    personeria2,
    representadoPorSpan,
    representado2PorSpan,
    vehPlateSpan,
  }
}

export const generateContractPdf = async (
  salenote: Salenote,
  userData: UserData,
  salenoteTradeIn?: Salenote,
  consignment?: Consignment,
  overrideAppraisal?: number
): Promise<File> => {
  return new Promise((resolve, reject) => {
    try {
      // 1. Prepare props
      const props = prepareContractData(salenote, userData, salenoteTradeIn, consignment)

      // 2. Create a hidden container with a wider canvas (900px) to comfortably fit PrintableCard's min-w-[800px]
      const container = document.createElement('div')
      container.style.position = 'fixed'
      container.style.top = '0'
      container.style.left = '0'
      container.style.width = '900px'
      container.style.zIndex = '-9999'
      container.style.backgroundColor = 'white'
      document.body.appendChild(container)
      // 3. Render the template with proper side margins (50px padding on 900px width = 800px available space) and clean style overrides
      const root = createRoot(container)
      root.render(
        <div style={{ backgroundColor: 'white', padding: '50px', width: '900px', boxSizing: 'border-box' }}>
          <style>{`
            /* Force fluid width inside the PDF render to prevent any off-screen overflow */
            .min-w-\\[800px\\] {
              min-width: 0 !important;
            }
            .printable-card {
              box-shadow: none !important;
              border: none !important;
              padding: 0 !important;
              margin: 0 !important;
              max-width: 100% !important;
              width: 100% !important;
            }
            /* Reset justified alignment and ugly word-breaks */
            .text-justify {
              text-align: justify !important;
              text-justify: inter-word !important;
              word-break: keep-all !important;
            }
            .hyphens-auto {
              hyphens: none !important;
              -webkit-hyphens: none !important;
              -moz-hyphens: none !important;
              -ms-hyphens: none !important;
            }
          `}</style>
          <SalenoteContractTemplate {...props} overrideAppraisal={overrideAppraisal} />
        </div>
      )
      // 4. Wait for render (short timeout to allow React to flush and fonts/images to load)
      setTimeout(async () => {
        try {
          // 5. Capture with html2canvas (forcing scroll position to (0,0), explicit width, and virtual window size to prevent viewport clipping)
          const canvas = await html2canvas(container, {
            scale: 2, // Higher scale for better quality
            useCORS: true, // For images
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: 900,
            width: 900,
          })
          // 6. Generate PDF
          console.log('PDF Gen: Canvas created', canvas.width, canvas.height)
          const imgData = canvas.toDataURL('image/jpeg', 0.95)

          // Use simpler constructor: orientation, unit, format
          // @ts-ignore - Handle potential type mismatch with different jspdf versions
          const pdf = new jsPDF('p', 'mm', 'a4')

          const imgWidth = 210 // A4 width in mm
          const pageHeight = 297 // A4 height in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width

          let heightLeft = imgHeight
          let position = 0

          console.log('PDF Gen: Adding first page')
          // Add first page
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
          heightLeft -= pageHeight

          // Add subsequent pages if needed
          while (heightLeft >= 0) {
            console.log('PDF Gen: Adding next page')
            position = heightLeft - imgHeight
            pdf.addPage()
            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight)
            heightLeft -= pageHeight
          }

          // 7. Convert to File object
          console.log('PDF Gen: Outputting blob')
          const blob = pdf.output('blob')
          const file = new File([blob], 'Borrador_Contrato.pdf', { type: 'application/pdf' })

          // Cleanup
          root.unmount()
          document.body.removeChild(container)

          resolve(file)
        } catch (err) {
          // Cleanup on error
          root.unmount()
          document.body.removeChild(container)
          reject(err)
        }
      }, 1000) // 1 second delay to ensure images load and React renders
    } catch (error) {
      reject(error)
    }
  })
}
